import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Loader2, ArrowLeft, Sparkles, CheckCircle2, Circle, Flame, Flag, HelpCircle } from "lucide-react";
import { ShareWinButton } from "@/components/app/ShareWin";

export default function GoalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [gen, setGen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/goals/${id}`);
    setD(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGen(true);
    try {
      const { data } = await api.post(`/goals/${id}/roadmap`);
      setRoadmap(data.roadmap);
      toast.success("Roadmap drafted — review and accept.");
    } catch (e) { !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail)); }
    finally { setGen(false); }
  };

  const accept = async () => {
    await api.post(`/goals/${id}/roadmap/accept`, { roadmap });
    setRoadmap(null);
    await load();
    toast.success("Roadmap applied to your plan.");
  };

  const completeTask = async (t) => { await api.post(`/tasks/${t.id}/complete`); await load(); };

  const goalWhy = (d && (d.goal?.why || d.goal?.description)) || "";
  const goToLearn = (itemTitle) => {
    const params = new URLSearchParams({
      help: "1",
      skill: itemTitle,
      focus: itemTitle,
    });
    if (goalWhy) params.set("why", goalWhy);
    navigate(`/app/learn?${params.toString()}`);
  };
  const logHabit = async (h) => { await api.post(`/habits/${h.id}/log`); await load(); toast.success("Streak up!"); };

  const setProgress = async (p) => {
    await api.post(`/goals/${id}/progress`, { progress: p });
    await load();
  };

  if (!d) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF7E67]" /></div>;
  const g = d.goal;
  const hasPlan = d.milestones.length || d.tasks.length || d.habits.length;

  return (
    <div className="space-y-8" data-testid="goal-detail">
      <button onClick={() => navigate("/app/goals")} className="flex items-center gap-1 text-sm text-[#718096] hover:text-[#2D3748]">
        <ArrowLeft className="h-4 w-4" /> All goals
      </button>

      <div className="rounded-3xl border border-[#FFE8E3] bg-white p-7">
        <span className="rounded-full border border-[#FFE8E3] px-3 py-1 text-xs capitalize text-[#FF7E67]">{(g.category || "").replace("_", " ")}</span>
        <h1 className="mt-3 font-nunito font-extrabold tracking-tight text-3xl text-[#2D3748] sm:text-4xl">{g.title}</h1>
        {g.why && <p className="mt-3 text-sm text-[#718096]">Why: {g.why}</p>}
        <div className="mt-5">
          <div className="mb-2 flex justify-between text-sm"><span className="text-[#718096]">Progress</span><span className="text-[#FF7E67]">{g.progress || 0}%</span></div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-[#FFE8E3]">
            <div className="h-full rounded-full bg-sunrise transition-all" style={{ width: `${g.progress || 0}%` }} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {[10, 25, 50, 75, 100].map((p) => (
              <button key={p} onClick={() => setProgress(p)} data-testid={`progress-${p}`}
                className="rounded-full border border-[#FFE8E3] px-3 py-1.5 text-xs text-[#4A5568] hover:border-[#FF7E67]">Set {p}%</button>
            ))}
          </div>
          {(g.progress || 0) >= 100 && (
            <div className="mt-4 flex items-center justify-between rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] p-4">
              <div>
                <p className="font-nunito text-sm font-bold text-[#C2410C]">Goal complete!</p>
                <p className="font-quicksand text-xs text-[#718096]">Celebrate — someone out there needs your inspiration.</p>
              </div>
              <ShareWinButton testid="goal-share-btn" label="Share this win"
                payload={{ kind: "goal", title: g.title, subtitle: g.why || "Goal 100% complete",
                           stat: "GOAL COMPLETE" }} />
            </div>
          )}
        </div>
      </div>

      {!hasPlan && (
        <div className="rounded-3xl border border-[#FFE8E3] bg-white p-8 text-center">
          <Sparkles className="mx-auto h-10 w-10 text-[#FF7E67]" />
          <p className="mt-3 text-[#718096]">No roadmap yet. Let your AI coach build one.</p>
          <button onClick={generate} disabled={gen} data-testid="generate-roadmap-btn"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-sunrise px-6 py-3 text-sm font-bold tracking-wide text-[#2D3748] disabled:opacity-70">
            {gen ? <Loader2 className="h-5 w-5 animate-spin" /> : "Generate AI Roadmap"}
          </button>
        </div>
      )}

      {roadmap && (
        <div className="rounded-3xl border border-[#FFB347] bg-[#FFF4E6] p-7" data-testid="roadmap-preview">
          <h2 className="font-nunito text-lg font-bold tracking-tight text-[#2D3748]">Proposed Roadmap</h2>
          {roadmap.definition && <p className="mt-2 text-sm text-[#4A5568]">{roadmap.definition}</p>}
          <p className="mt-4 text-sm text-[#718096]">Milestones: {roadmap.milestones?.length} • Habits: {roadmap.habits?.length} • Week-1 tasks: {(roadmap.first_week_plan || []).reduce((n, x) => n + (x.tasks?.length || 0), 0)}</p>
          <div className="mt-4 flex gap-3">
            <button onClick={accept} data-testid="accept-roadmap-btn" className="rounded-full bg-sunrise px-6 py-2.5 text-sm font-bold text-[#2D3748]">Accept & Apply</button>
            <button onClick={() => setRoadmap(null)} className="rounded-full border border-[#FFE8E3] px-6 py-2.5 text-sm text-[#4A5568]">Discard</button>
          </div>
        </div>
      )}

      {d.milestones.length > 0 && (
        <Section title="Milestones">
          {d.milestones.map((m) => (
            <div key={m.id} className="flex items-start gap-3 rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-3">
              <Flag className="mt-0.5 h-4 w-4 shrink-0 text-[#FF7E67]" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#2D3748]">{m.title}</p>
                {m.target_date && <p className="text-xs text-[#718096]">{m.target_date}</p>}
              </div>
              <button
                onClick={() => goToLearn(m.title)}
                data-testid={`help-milestone-${m.id}`}
                title="Learn how to complete this"
                className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#FFE8E3] bg-white px-3 py-1 font-nunito text-[11px] font-bold text-[#C2410C] hover:border-[#FF7E67] hover:bg-[#FFF4EF]"
              >
                <HelpCircle className="h-3 w-3" strokeWidth={2.5} /> I need help with this
              </button>
            </div>
          ))}
        </Section>
      )}

      {d.habits.length > 0 && (
        <Section title="Habits">
          {d.habits.map((h) => (
            <button key={h.id} onClick={() => logHabit(h)} className="flex w-full items-center justify-between rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-3 hover:border-[#FF7E67]">
              <span className="text-sm text-[#2D3748]">{h.name}</span>
              <span className="flex items-center gap-1 text-xs text-[#FF7E67]"><Flame className="h-4 w-4" />{h.streak}</span>
            </button>
          ))}
        </Section>
      )}

      {d.tasks.length > 0 && (
        <Section title="Tasks">
          {d.tasks.map((t) => (
            <div key={t.id}
              className="flex items-center gap-3 rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-3 hover:border-[#FF7E67]">
              <button onClick={() => t.status !== "done" && completeTask(t)}
                data-testid={`toggle-task-${t.id}`}
                title={t.status === "done" ? "Completed" : "Mark done"}
                className="shrink-0">
                {t.status === "done"
                  ? <CheckCircle2 className="h-5 w-5 text-[#FF7E67]" />
                  : <Circle className="h-5 w-5 text-[#A0AEC0]" />}
              </button>
              <span className={`flex-1 truncate text-sm ${t.status === "done" ? "text-[#A0AEC0] line-through" : "text-[#2D3748]"}`}>{t.title}</span>
              {t.status !== "done" && (
                <button
                  onClick={() => goToLearn(t.title)}
                  data-testid={`help-task-${t.id}`}
                  title="Learn how to complete this"
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#FFE8E3] bg-white px-3 py-1 font-nunito text-[11px] font-bold text-[#C2410C] hover:border-[#FF7E67] hover:bg-[#FFF4EF]"
                >
                  <HelpCircle className="h-3 w-3" strokeWidth={2.5} /> I need help
                </button>
              )}
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="rounded-3xl border border-[#FFE8E3] bg-white p-6">
    <h2 className="mb-4 font-nunito text-lg font-bold tracking-tight text-[#2D3748]">{title}</h2>
    <div className="space-y-2">{children}</div>
  </div>
);
